import React, { FC } from "react";
import {
  ClientValue,
  NewClientValue,
  MatterType,
  ExternalMatterData,
  PracticeArea,
  ClientFilterData,
  MatterFormMode,
  RelevantMatter,
  PricingType,
  PricingLevel,
  PricingTarget,
} from "../../data-types";
import { FormInstance, Typography, Button, Form, Checkbox, Row, Col } from "antd";
import { MatterCreationOrEditingForm } from "../matter-creation-or-editing-form";
import { CreateProposalScreens } from "./create-proposal";
import { pricingStructureToCascaderValue } from "../pricing-structure-options/pricing-structure-options";
import { BookOutlined, ClockCircleOutlined } from "@ant-design/icons";
import styles from "./create-proposal.module.css";

const { ["matter-source-indicator"]: matterSourceIndicatorClassName } = styles;

export type MatterDetailsScreenProps = {
  selectedMatter: RelevantMatter | null;
  setSelectedMatter: (matter: RelevantMatter | null) => void;
  setScreen: (screen: CreateProposalScreens) => void;
  client?: ClientValue | null | undefined;
  setCurrentClient?: ((client: ClientValue | null | undefined) => void) | undefined;
  template: MatterType;
  parentMatterId?: number | undefined;
  form: FormInstance;
  externalMatterData: ExternalMatterData;
  pmsName: string;
  practiceAreas: PracticeArea[];
  onClientSave: (newClient: NewClientValue) => void;
  clientFilterData: ClientFilterData;
  feeEarnersCascaderRender: () => React.ReactNode;
  mode: MatterFormMode;
  selectedCurrencyId?: number;
};

export const MatterDetailsScreen: FC<MatterDetailsScreenProps> = ({
  selectedMatter,
  setSelectedMatter,
  setScreen,
  client,
  setCurrentClient,
  template,
  parentMatterId,
  form,
  externalMatterData,
  pmsName,
  practiceAreas,
  onClientSave,
  clientFilterData,
  feeEarnersCascaderRender,
  mode,
  selectedCurrencyId,
}) => {
  return (
    <div style={{ padding: "0 20px" }}>
      {selectedMatter && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span className={matterSourceIndicatorClassName}>
            {selectedMatter?.template ? (
              <BookOutlined style={{ marginRight: 8 }} />
            ) : (
              <ClockCircleOutlined style={{ marginRight: 8 }} />
            )}
            <Typography.Text>
              <span style={{ fontWeight: "bold" }}>
                Creating from {selectedMatter.template ? "template" : "historical matter"}:{" "}
              </span>
              {selectedMatter.title}
            </Typography.Text>
          </span>
          <Button
            style={{ marginLeft: "10px" }}
            onClick={() => {
              setSelectedMatter(null);
              form.resetFields();
              setScreen(CreateProposalScreens.MatterTitle);
            }}
          >
            Change
          </Button>
        </div>
      )}
      {selectedMatter && !selectedMatter.template && (
        <Form form={form} style={{ marginTop: "20px", marginBottom: "20px" }}>
          <Row align="middle" gutter={8}>
            <Col flex="none">
              <Typography.Text>Copy from historical matter:</Typography.Text>
            </Col>
            <Col flex="auto">
              <Row gutter={[16, 0]}>
                <Col>
                  <Form.Item name="copyScope" valuePropName="checked" noStyle>
                    <Checkbox>Scope</Checkbox>
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item name="copyResourcing" valuePropName="checked" noStyle>
                    <Checkbox>Resourcing</Checkbox>
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item name="copyPricing" valuePropName="checked" noStyle>
                    <Checkbox
                      onChange={(e) => {
                        if (e.target.checked && selectedMatter) {
                          form.setFieldsValue({
                            pricingStructure: pricingStructureToCascaderValue({
                              pricingType: selectedMatter.pricingType,
                              pricingLevel: selectedMatter.pricingLevel,
                              pricingTarget: selectedMatter.pricingTarget,
                            }),
                          });
                        } else {
                          form.setFieldsValue({
                            pricingStructure: null,
                          });
                        }
                      }}
                    >
                      Pricing
                    </Checkbox>
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      )}
      <div style={{ maxWidth: "700px" }}>
        <MatterCreationOrEditingForm
          client={client}
          setCurrentClient={setCurrentClient}
          template={template}
          parentMatterId={parentMatterId}
          form={form}
          externalMatterData={externalMatterData}
          pmsName={pmsName}
          practiceAreas={practiceAreas}
          onClientSave={onClientSave}
          clientFilterData={clientFilterData}
          feeEarnersCascaderRender={feeEarnersCascaderRender}
          mode={mode}
          selectedCurrencyId={selectedCurrencyId}
          // if creating from historical matter, do not allow changing currency - it must match the historical matter
          allowChangeCurrency={
            !(selectedCurrencyId !== undefined && selectedMatter && !selectedMatter.template)
          }
        />
      </div>
    </div>
  );
};
